import type { Renderer } from '../render/renderer';
import { wrapDelta } from '../sim/world';

const MAX = 96;
const LIFE = 0.7;

/**
 * Floating damage numbers: a fixed pool of absolutely-positioned DOM labels
 * projected from world space each frame. Toggle with `enabled`.
 */
export class DamageNumbers {
  enabled = true;
  private els: HTMLElement[] = [];
  private x = new Float32Array(MAX);
  private z = new Float32Array(MAX);
  private t = new Float32Array(MAX);
  private cursor = 0;
  private root: HTMLElement;
  private vec: ReturnType<Renderer['makeVec']>;

  constructor(parent: HTMLElement, private renderer: Renderer) {
    this.root = document.createElement('div');
    this.root.id = 'dmgnums';
    parent.appendChild(this.root);
    for (let i = 0; i < MAX; i++) {
      const el = document.createElement('div');
      el.className = 'dmg';
      el.style.display = 'none';
      this.root.appendChild(el);
      this.els.push(el);
    }
    this.vec = renderer.makeVec();
  }

  spawn(x: number, z: number, dmg: number, crit: boolean, side: 0 | 1): void {
    if (!this.enabled) return;
    const i = this.cursor;
    this.cursor = (this.cursor + 1) % MAX;
    this.x[i] = x + (Math.random() - 0.5) * 0.6;
    this.z[i] = z + (Math.random() - 0.5) * 0.6;
    this.t[i] = LIFE;
    const el = this.els[i];
    el.textContent = Math.round(dmg).toString();
    el.className = 'dmg' + (crit ? ' crit' : '') + (side === 1 ? ' hurt' : '');
    el.style.display = '';
  }

  update(dt: number, camX: number, camZ: number, half: number): void {
    const w = innerWidth;
    const h = innerHeight;
    for (let i = 0; i < MAX; i++) {
      if (this.t[i] <= 0) continue;
      this.t[i] -= dt;
      const el = this.els[i];
      if (this.t[i] <= 0 || !this.enabled) {
        el.style.display = 'none';
        this.t[i] = 0;
        continue;
      }
      const f = 1 - this.t[i] / LIFE;
      const wx = camX + wrapDelta(this.x[i] - camX, half);
      const wz = camZ + wrapDelta(this.z[i] - camZ, half);
      this.renderer.project(wx, 1.4 + f * 1.6, wz, this.vec);
      const sx = (this.vec.x * 0.5 + 0.5) * w;
      const sy = (-this.vec.y * 0.5 + 0.5) * h;
      el.style.transform = `translate(${sx.toFixed(0)}px, ${sy.toFixed(0)}px) translate(-50%, -50%) scale(${(1.15 - f * 0.35).toFixed(2)})`;
      el.style.opacity = f < 0.7 ? '1' : ((1 - f) / 0.3).toFixed(2);
    }
  }

  clear(): void {
    for (let i = 0; i < MAX; i++) {
      this.t[i] = 0;
      this.els[i].style.display = 'none';
    }
  }
}
